// src/sections/CMS/components/customPage/FeatureBadgesRenderer.tsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTypography } from '../../../../context/TypographyContext';
import { getFileServerBase } from './fileServerBase';

const FILE_SERVER_BASE = `${getFileServerBase()}/media/large/`;

const resolveMediaUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${FILE_SERVER_BASE}${url}`;
};

// ─── TYPES ───────────────────────────────────────────────────────

// A suitability symbol as returned by the CMS page response (symbols.web_suitability).
interface SuitabilitySymbol {
  symbol_name: string;
  symbol_description?: string | null;
  file_name?: string;
  file_url?: string;
}

// Manual copy authored in the hub Feature Badges block (badge_items).
interface BadgeDescription {
  id?: string;
  symbol_name?: string;
  description?: string;
}

interface FeatureBadgesRendererProps {
  // The page's `symbols` object. Badges come from its web_suitability and
  // symbols groups only (see SYMBOL_GROUPS).
  symbols: Record<string, any> | null;
  // Manual descriptions authored in the hub (badge_items).
  descriptions?: BadgeDescription[];
  // Link text for the modal trigger (falls back to "Learn more about badges").
  learnMoreText?: string;
  // Optional max width for the component (CSS value, e.g. "800px"); centered when set.
  maxWidth?: string;
  // Show the "Learn more" link + lightbox. Default true (Hertex set badges).
  // Pass false for HAUS set badges: they display the badge images only.
  showLearnMore?: boolean;
  // Lightbox heading (falls back to "Performance badges").
  modalTitle?: string;
}

// Symbol groups shown as feature badges, in display order. Standards (Oeko-Tex,
// REACH) and cleaning instructions are deliberately excluded: they are not
// performance badges.
const SYMBOL_GROUPS = ['web_suitability', 'symbols'];

// Mobile = below MUI's md breakpoint (900px). Used to override P1/H2 typography.
const MOBILE = '@media (max-width:899.95px)';

const flattenSymbols = (symbols: Record<string, any> | null | undefined): SuitabilitySymbol[] => {
  if (!symbols) return [];
  const seenNames = new Set<string>();
  const seenImages = new Set<string>();
  const out: SuitabilitySymbol[] = [];
  SYMBOL_GROUPS.forEach((group) => {
    const arr = symbols[group];
    if (!Array.isArray(arr)) return;
    arr.forEach((s: SuitabilitySymbol) => {
      const name = s?.symbol_name?.trim();
      const img = s?.file_url || s?.file_name;
      // No image means there is nothing to display, so skip it.
      if (!name || !img) return;
      // Dedupe by name (case-insensitive) and by image, because the CMS can
      // return the same symbol under different names (e.g. "Dry Clean" and
      // "Dry Clean Only" share one icon).
      const nameKey = name.toLowerCase();
      if (seenNames.has(nameKey) || seenImages.has(img)) return;
      seenNames.add(nameKey);
      seenImages.add(img);
      out.push(s);
    });
  });
  return out;
};

const badgeImage = (b: SuitabilitySymbol): string =>
  resolveMediaUrl(b.file_url || b.file_name);

// ─── MAIN RENDERER ───────────────────────────────────────────────

const FeatureBadgesRenderer: React.FC<FeatureBadgesRendererProps> = ({
                                                                       symbols,
                                                                       descriptions = [],
                                                                       learnMoreText,
                                                                       maxWidth,
                                                                       showLearnMore = true,
                                                                       modalTitle,
                                                                     }) => {
  const { styleFor } = useTypography();
  const [open, setOpen] = useState(false);

  // Badges in the order authored in the hub. badge_items (descriptions) carries
  // the drag-and-drop order; symbols are shown in that order, and any symbol not
  // present in badge_items keeps its natural order, appended at the end.
  const badges = useMemo(() => {
    const flat = flattenSymbols(symbols);
    if (!Array.isArray(descriptions) || descriptions.length === 0) return flat;
    const pos = new Map<string, number>();
    descriptions.forEach((d, i) => {
      if (d.symbol_name && !pos.has(d.symbol_name)) pos.set(d.symbol_name, i);
    });
    return flat
      .map((b, i) => ({
        b,
        i,
        p: pos.has(b.symbol_name) ? (pos.get(b.symbol_name) as number) : Number.MAX_SAFE_INTEGER,
      }))
      .sort((a, z) => a.p - z.p || a.i - z.i)
      .map((x) => x.b);
  }, [symbols, descriptions]);

  // Match a manually-written description to a badge by symbol_name.
  const descByName = useMemo(() => {
    const map = new Map<string, string>();
    descriptions.forEach((d) => {
      if (d.symbol_name && d.description) map.set(d.symbol_name, d.description);
    });
    return map;
  }, [descriptions]);

  const descriptionFor = (badge: SuitabilitySymbol): string =>
    descByName.get(badge.symbol_name) || badge.symbol_description || '';

  if (!Array.isArray(badges) || badges.length === 0) return null;

  const linkText = learnMoreText?.trim() || 'Learn more about badges';

  return (
    <Container maxWidth="lg" disableGutters sx={maxWidth ? { maxWidth: `${maxWidth} !important` } : undefined}>
      {/* ── Badge strip (all badges in one line) ─────────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: { xs: 2, md: 2.5 },
        }}
      >
        {badges.map((badge) => {
          const src = badgeImage(badge);
          if (!src) return null;
          return (
            <Box
              key={badge.symbol_name}
              component="img"
              src={src}
              alt={badge.symbol_name}
              sx={{
                width: { xs: 60, md: 100 },
                height: { xs: 60, md: 100 },
                objectFit: 'contain',
                borderRadius: '50%',
                flexShrink: 0,
              }}
            />
          );
        })}
      </Box>

      {showLearnMore && (
        <>
          {/* ── Learn more trigger ───────────────────────────────────── */}
          <Link
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            sx={{
              mt: { xs: 2, md: 2.5 },
              display: 'inline-block',
              ...styleFor('P1'),
              fontWeight: 700,
              color: 'text.primary',
              textDecoration: 'underline',
              textDecorationColor: 'currentColor',
              [MOBILE]: { fontSize: '14px' },
              cursor: 'pointer',
            }}
          >
            {linkText}
          </Link>

          {/* ── Modal (matches the global search multi-filter modal) ──── */}
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: {
                width: '90vw',
                height: '90vh',
                maxWidth: '90vw',
                maxHeight: '90vh',
                margin: '5vh',
                borderRadius: '12px',
                overflow: 'hidden',
                [MOBILE]: {
                  width: 'calc(100vw - 32px)',
                  maxWidth: 'calc(100vw - 32px)',
                  height: 'calc(100dvh - 32px)',
                  maxHeight: 'calc(100dvh - 32px)',
                  margin: '16px',
                  borderRadius: '8px',
                },
              },
            }}
          >
            {/* Header with close button */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 2, md: 5 },
                pt: { xs: 2, md: 3 },
                pb: 0,
              }}
            >
              <Typography sx={{ ...styleFor('H2'), color: '#333', [MOBILE]: { fontSize: '24px', lineHeight: 1.2 } }}>
                {modalTitle?.trim() || 'Performance badges'}
              </Typography>
              <IconButton
                onClick={() => setOpen(false)}
                aria-label="Close"
                sx={{ color: '#666', '&:hover': { backgroundColor: '#e0e0e0' } }}
              >
                <Close sx={{ fontSize: { xs: 24, md: 32 } }} />
              </IconButton>
            </Box>

            <DialogContent sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 5 }, pt: 0, pb: { xs: 3, md: 5 } }}>
              {badges.map((badge, index) => {
                const src = badgeImage(badge);
                const copy = descriptionFor(badge);
                return (
                  <Box key={badge.symbol_name} sx={{ pt: { xs: index === 0 ? '20px' : '32px', md: index === 0 ? '30px' : '45px' } }}>
                    {src && (
                      <Box
                        component="img"
                        src={src}
                        alt={badge.symbol_name}
                        sx={{ width: { xs: 56, md: 72 }, height: { xs: 56, md: 72 }, objectFit: 'contain', borderRadius: '50%', display: 'block', mb: { xs: '16px', md: '20px' } }}
                      />
                    )}
                    <Typography
                      sx={{ ...styleFor('P1'), fontSize: { xs: '14px', md: '18px' }, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}
                    >
                      {badge.symbol_name}
                    </Typography>
                    {copy && (
                      <Typography sx={{ ...styleFor('P1'), fontSize: { xs: '14px', md: '18px' } }}>
                        {copy}
                      </Typography>
                    )}
                    <Divider sx={{ mt: { xs: '32px', md: '45px' }, borderColor: 'rgba(0,0,0,0.4)' }} />
                  </Box>
                );
              })}
            </DialogContent>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export default FeatureBadgesRenderer;